/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Modal, Button } from "antd";
import { useReactToPrint } from "react-to-print";
import { PiPrinterFill } from "react-icons/pi";
import logo from "../../../assets/images/logo_nsf.png";
import PurchaseOrderService from "../../../service/ERP/PurchaseOrder.service";
import dayjs from "dayjs";
import "./po.css";

const poService = PurchaseOrderService();

const num = (v) => Number(v || 0);
const money = (v, d = 2) =>
  num(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

/* วันที่แบบไทยย่อ เช่น 21/10/68 (พ.ศ.) */
const thaiDate = (d) =>
  d ? `${dayjs(d).format("DD/MM/")}${(dayjs(d).year() + 543) % 100}` : "-";

/* ── จำนวนเงินเป็นตัวอักษรไทย ── */
const bahtText = (amount) => {
  const t = ["ศูนย์", "หนึ่ง", "สอง", "สาม", "สี่", "ห้า", "หก", "เจ็ด", "แปด", "เก้า"];
  const p = ["", "สิบ", "ร้อย", "พัน", "หมื่น", "แสน"];
  const readGroup = (raw) => {
    const s = raw.replace(/^0+/, "");
    if (!s) return "";
    let out = "";
    const len = s.length;
    for (let i = 0; i < len; i++) {
      const d = +s[i];
      const pos = len - i - 1;
      if (!d) continue;
      if (pos === 0 && d === 1 && len > 1) out += "เอ็ด";
      else if (pos === 1 && d === 2) out += "ยี่สิบ";
      else if (pos === 1 && d === 1) out += "สิบ";
      else out += t[d] + p[pos];
    }
    return out;
  };
  const readNum = (s) => {
    s = s.replace(/^0+/, "") || "0";
    if (s === "0") return "";
    if (s.length > 6) return readNum(s.slice(0, -6)) + "ล้าน" + readGroup(s.slice(-6));
    return readGroup(s);
  };
  const [intPart, decPart] = num(amount).toFixed(2).split(".");
  let txt = readNum(intPart);
  txt = txt ? txt + "บาท" : "ศูนย์บาท";
  if (decPart === "00") txt += "ถ้วน";
  else txt += readNum(decPart) + "สตางค์";
  return txt;
};

/* จำนวนแถวสูงสุดที่พื้นที่ตารางใน 1 หน้า A4 รองรับได้
   (คำนวณจาก 297mm - ขอบ 16mm - หัวจดหมาย/กล่องข้อมูล ~88mm - สรุป/ลายเซ็น ~69mm) */
const ITEMS_PER_PAGE = 13;

/* แบ่งรายการเป็นหน้า + เติมแถวว่างให้หน้าสุดท้ายสูงเท่ากัน */
const buildPages = (rows) => {
  if (!rows.length) return [[]];
  const pages = [];
  for (let i = 0; i < rows.length; i += ITEMS_PER_PAGE) {
    pages.push(rows.slice(i, i + ITEMS_PER_PAGE));
  }
  return pages;
};

export default function PoPrintPreview() {
  const { code } = useParams();
  const wrapRef  = useRef(null);
  const docRef   = useRef(null);
  const printRef = useRef(null);
  const [header, setHeader] = useState({});
  const [details, setDetails] = useState([]);
  const [services, setServices] = useState([]);

  const handlePrint = useReactToPrint({
    documentTitle: `${code}`,
    removeAfterPrint: true,
  });

  /* ── scale ตาม width ── */
  useEffect(() => {
    const fit = () => {
      const wrap   = wrapRef.current;
      const doc    = docRef.current;
      const layout = printRef.current;
      if (!wrap || !doc || !layout) return;
      const availW = wrap.clientWidth - 32;
      const scale  = Math.min(availW / layout.offsetWidth, 1) * 0.85;
      doc.style.transform       = `scale(${scale})`;
      doc.style.transformOrigin = "top center";
      doc.style.marginBottom    = `${(scale - 1) * layout.offsetHeight}px`;
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [header, details, services]);

  useEffect(() => {
    poService.get(code).then((res) => {
      const { header: h, detail: d, services: sv } = res.data || {};
      setHeader(h || {});
      setDetails(d || []);
      setServices(sv || []);
    }).catch((err) => {
      Modal.error({
        title: "Error",
        content: err.response?.data?.message || "Request failed. Please try again.",
        onOk: () => window.close(),
      });
    });
  }, [code]);

  /* ค่าบริการเพิ่มต่อท้ายรายการสินค้า นับลำดับต่อกันไปเลย
     (ไม่มีจำนวน/ราคาต่อหน่วย มีแต่จำนวนเงิน) */
  const serviceRows = (services || []).map((v) => ({
    is_service: true,
    stcode: v.service_code || "",
    stname: v.service_name || "",
    unit: "",
    qty: null,
    price: null,
    discount: 0,
    amount: num(v.amount),
  }));
  const serviceTotal = serviceRows.reduce((a, v) => a + num(v.amount), 0);
  const rows = [...details, ...serviceRows];

  const grossAmount    = details.reduce((a, d) => a + num(d.qty) * num(d.price), 0) + serviceTotal;
  /* ส่วนลด = ส่วนลดรายบรรทัด + ส่วนลดรวมท้ายบิล (ใช้ตัวเลขที่บันทึกไว้ ไม่คำนวณจาก %) */
  const lineDiscount   = details.reduce((a, d) => a + num(d.discount), 0);
  const discountTotal  = lineDiscount + num(header.total_discount);
  const afterDiscount  = grossAmount - discountTotal;
  const vatPercent     = num(header.vat);
  const vatAmount      = (afterDiscount * vatPercent) / 100;
  const netAmount      = afterDiscount + vatAmount;

  const pages = buildPages(rows);
  const totalPages = pages.length;

  return (
    <div className="page-show" id="po" ref={wrapRef}>
      <div className="doc-wrapper" ref={docRef}>
        <div className="print-btn no-print">
          <Button
            className="bn-center bg-blue-400"
            icon={<PiPrinterFill style={{ fontSize: "1.1rem" }} />}
            onClick={() => handlePrint(null, () => wrapRef.current)}
          >
            PRINT
          </Button>
        </div>

        {pages.map((pageRows, pageIndex) => {
        const isLastPage = pageIndex === totalPages - 1;
        const startNo = pageIndex * ITEMS_PER_PAGE;
        return (
        <div
          className="print-layout"
          key={pageIndex}
          ref={pageIndex === 0 ? printRef : undefined}
        >

          {/* ── Letterhead ── */}
          <div className="po-head">
            <img src={logo} alt="logo" className="po-logo" />
            <div className="po-company">
              <p className="po-co-name">บริษัท ไนน์สตาร์ฟู้ด จำกัด</p>
              <p className="po-co-addr">99/9 หมู่ 8 ตำบลกองดิน อำเภอแกลง จังหวัดระยอง 22160</p>
              <p className="po-co-addr">Tel:095-7577408,091-8730940,038-015189</p>
            </div>
          </div>

          {/* ── Tax ID + Title ── */}
          <div className="po-tax-title">
            <div className="po-tax">
              <span>เลขประจำตัวผู้เสียภาษี/ Tax ID</span>
              <span className="po-tax-no">0225556000121</span>
              <span>สาขาที่ 00001</span>
            </div>
            <div className="po-title">
              <p className="po-title-main">ใบสั่งซื้อ/ Purchase Order</p>
              <p className="po-title-sub">
                ต้นฉบับ/Original
                {totalPages > 1 && ` (หน้า ${pageIndex + 1}/${totalPages})`}
              </p>
            </div>
          </div>

          {/* ── Supplier + PO Info boxes ── */}
          <div className="po-boxes">
            <div className="po-box po-box-supplier">
              <div className="po-box-row">
                <span className="po-label">ผู้จำหน่าย<br /><em>Supplier</em></span>
                <span className="po-value">{header.supcode}</span>
              </div>
              <div className="po-supname">{header.supname}</div>
              <div className="po-supaddr">{header.address}</div>
              <div className="po-box-row">
                <span className="po-label">โทร.<br /><em>Tel.</em></span>
                <span className="po-value">
                  {header.tel}{header.contact ? ` ${header.contact}` : ""}
                </span>
              </div>
              <div className="po-box-row">
                <span className="po-label">อ้างอิง<br /><em>Reference</em></span>
                <span className="po-value">{header.poqua || ""}</span>
              </div>
            </div>
            <div className="po-box po-box-info">
              <div className="po-box-row">
                <span className="po-label">เลขที่ใบสั่งซื้อ<br /><em>P/O No.</em></span>
                <span className="po-value po-pocode">{header.pocode}</span>
              </div>
              <div className="po-box-row">
                <span className="po-label">วันที่<br /><em>Date</em></span>
                <span className="po-value">{thaiDate(header.podate)}</span>
              </div>
              <div className="po-box-row">
                <span className="po-label">วันที่รับของ<br /><em>Required Date</em></span>
                <span className="po-value">{thaiDate(header.deldate)}</span>
              </div>
              <div className="po-box-row">
                <span className="po-label">เครดิต<br /><em>Credit</em></span>
                <span className="po-value">
                  {header.payment ? <span className="po-credit-pill">{header.payment}</span> : "-"}
                </span>
              </div>
              <div className="po-box-row">
                <span className="po-label">ขนส่งโดย<br /><em>Delivery by</em></span>
                <span className="po-value">{header.ship_to || ""}</span>
              </div>
            </div>
          </div>

          {/* ── Detail Table ── */}
          <div className="po-content">
            <table className="po-table">
              <colgroup>
                <col style={{ width: "12mm" }} />
                <col />
                <col style={{ width: "34mm" }} />
                <col style={{ width: "26mm" }} />
                <col style={{ width: "30mm" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>ลำดับ<br /><em>No.</em></th>
                  <th>รหัสสินค้า/รายละเอียด<br /><em>Code/ Descriptions</em></th>
                  <th>จำนวน<br /><em>Quantity</em></th>
                  <th>หน่วยละ<br /><em>Unit Price</em></th>
                  <th>จำนวนเงิน<br /><em>Amount</em></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((d, i) => (
                  <tr key={i}>
                    <td className="txt-c">{startNo + i + 1}</td>
                    <td className="txt-l">{d.stcode} {d.stname}</td>
                    <td className="txt-r">
                      {d.is_service ? "" : <>{money(d.qty)}&nbsp;&nbsp;{d.unit}</>}
                    </td>
                    <td className="txt-r">{d.is_service ? "" : money(d.price)}</td>
                    <td className="txt-r">
                      {d.is_service
                        ? money(d.amount)
                        : money(num(d.qty) * num(d.price) - num(d.discount))}
                    </td>
                  </tr>
                ))}
                {!isLastPage && (
                  <tr className="po-carry">
                    <td></td>
                    <td className="txt-l">ยกยอดไปหน้าถัดไป / Carried forward</td>
                    <td></td><td></td><td></td>
                  </tr>
                )}
                <tr className="po-filler"><td></td><td></td><td></td><td></td><td></td></tr>
              </tbody>
            </table>
          </div>

          {/* ── Summary + Net + Signatures : เฉพาะหน้าสุดท้าย ── */}
          {isLastPage && (
          <>
          <div className="po-summary">
            <div className="po-remark">
              <span className="po-label">หมายเหตุ<br /><em>Remark</em></span>
              <div className="po-remark-text">{header.remark || ""}</div>
            </div>
            <div className="po-totals">
              <div className="po-total-row">
                <span className="po-label">รวมเป็นเงิน<br /><em>Gross Amount</em></span>
                <span className="po-total-val">{money(grossAmount)}</span>
              </div>
              <div className="po-total-row">
                <span className="po-label">หักส่วนลด<br /><em>Less Discount</em></span>
                <span className="po-total-val">{money(discountTotal)}</span>
              </div>
              <div className="po-total-row">
                <span className="po-label">ยอดหลังหักส่วนลด<br /><em>After Discount</em></span>
                <span className="po-total-val">{money(afterDiscount)}</span>
              </div>
              <div className="po-total-row">
                <span className="po-label">จำนวนภาษีมูลค่าเพิ่ม<br /><em>VAT</em></span>
                <span className="po-vat-pct">{vatPercent.toFixed(2)}&nbsp;&nbsp;%</span>
                <span className="po-total-val">{money(vatAmount)}</span>
              </div>
            </div>
          </div>

          {/* ── Net Amount band ── */}
          <div className="po-net">
            <div className="po-net-word">({bahtText(netAmount)}).</div>
            <div className="po-net-label">
              <span className="po-label">จำนวนเงินรวมทั้งสิ้น<br /><em>Net Amount</em></span>
            </div>
            <div className="po-net-val">{money(netAmount)}</div>
          </div>

          {/* ── Signatures ── */}
          <div className="po-foot">
            <div className="po-sign-left">
              <div className="po-sign-block">
                <div className="po-sign-line"></div>
                <div className="po-sign-caption">ผู้สั่งซื้อสินค้า/ Order by</div>
              </div>
              <div className="po-sign-block">
                <div className="po-sign-line"></div>
                <div className="po-sign-caption">วันที่/ Date</div>
              </div>
            </div>
            <div className="po-sign-right">
              <div className="po-inname">
                ในนาม&nbsp;&nbsp;<strong>บริษัท ไนน์สตาร์ฟู้ด จำกัด</strong>
              </div>
              <div className="po-sign-block">
                <div className="po-sign-line"></div>
                <div className="po-sign-caption">ผู้รับมอบอำนาจ/ Authorized Signature</div>
              </div>
            </div>
          </div>
          </>
          )}

        </div>
        );
        })}
      </div>
    </div>
  );
}
